import React, { useState, useEffect } from "react";
import Modal from "../../../../components/Modal";
import Icon from "../../../../components/Icon";
import { buildFullWebhookUrl } from "../../services/IntegrationsService";

const SETUP_GUIDES = {
  jira: {
    name: "Jira",
    title: "Atlassian Jira Integration Guide",
    subtitle: "Real-time bidirectional issue lifecycle synchronization and automated ticket management.",
    prerequisites: [
      "Jira Cloud or Data Center Base URL (e.g. https://your-domain.atlassian.net)",
      "Atlassian user email or service account identifier",
      "Atlassian API Token generated from your Atlassian security profile",
      "Jira project administrator or system administrator permissions",
    ],
    steps: [
      {
        step: 1,
        title: "Connect Jira in AIOps",
        desc: "Click 'Connect Jira' below. Enter your Jira instance URL, user email, and Atlassian API token, then run the connection test to verify credentials.",
      },
      {
        step: 2,
        title: "Receive your Instance Webhook URL",
        desc: "Once saved, AIOps automatically provisions a dedicated, cryptographically signed incoming webhook pipeline URL for your Jira instance, accessible on your integration card under Configured Integrations.",
      },
      {
        step: 3,
        title: "Open Jira Webhooks Settings",
        desc: "In Jira Cloud or Server, navigate to Settings (⚙️) > System > WebHooks (located under Advanced in the left navigation).",
      },
      {
        step: 4,
        title: "Register New Webhook in Jira",
        desc: "Click '+ Create a Webhook' in the top-right corner. Enter a name (e.g. 'Cxsphere AIOps Sync') and paste your dedicated AIOps Webhook URL into the URL field.",
      },
      {
        step: 5,
        title: "Subscribe to Issue Lifecycle Events",
        desc: "Under 'Issue related events', enable the following event checkboxes:",
        bullets: [
          "Issue: Created — automatically provisions incident investigation tickets in AIOps",
          "Issue: Updated — synchronizes status transitions, assignees, and priority changes",
          "Issue: Deleted — cleans up or archives corresponding incident tickets",
          "Comment: Created — synchronizes root-cause analysis notes and team updates",
        ],
      },
      {
        step: 6,
        title: "Save & Activate",
        desc: "Click 'Save' at the bottom of the form in Jira. Events will now stream into AIOps immediately in real-time!",
      },
    ],
    samplePayload: {
      webhookEvent: "jira:issue_updated",
      issue: {
        id: "10042",
        key: "AIOPS-101",
        fields: {
          summary: "Service degradation detected on core API",
          status: { name: "In Progress" },
          priority: { name: "High" },
        },
      },
    },
  },
  servicenow: {
    name: "ServiceNow",
    title: "ServiceNow ITSM Integration Guide",
    subtitle: "Automate incident triage, correlate telemetry, and bidirectional incident record sync.",
    prerequisites: [
      "ServiceNow Instance URL (e.g. https://dev12345.service-now.com)",
      "Integration service account with 'itil' and 'rest_service' or 'web_service_admin' roles",
      "Basic Auth credentials or OAuth client access",
      "Administrator or developer permissions to configure Outbound REST or Business Rules",
    ],
    steps: [
      {
        step: 1,
        title: "Connect ServiceNow in AIOps",
        desc: "Click 'Connect ServiceNow' below. Enter your instance URL, username, and password, then test connectivity and save.",
      },
      {
        step: 2,
        title: "Receive your Instance Webhook URL",
        desc: "Upon saving, your dedicated incoming Webhook URL and secret token will be provisioned and displayed on your configured integration card.",
      },
      {
        step: 3,
        title: "Configure Outbound REST Message or Business Rule",
        desc: "In ServiceNow Filter Navigator, go to System Web Services > Outbound > REST Messages (or create a Business Rule on the 'incident' table).",
      },
      {
        step: 4,
        title: "Set REST Endpoint & Headers",
        desc: "Set the HTTP Method to POST, enter your dedicated AIOps Webhook URL as the Endpoint, and add header 'Content-Type: application/json'.",
      },
      {
        step: 5,
        title: "Set Trigger Conditions",
        desc: "In your Business Rule, set execution timing to 'After' (or 'Async') on 'Insert' and 'Update' for incidents requiring AIOps monitoring.",
      },
      {
        step: 6,
        title: "Save & Activate",
        desc: "Save and activate the rule in ServiceNow. Incidents will now automatically trigger root-cause analysis and telemetry correlation in AIOps!",
      },
    ],
    samplePayload: {
      number: "INC0091024",
      sys_id: "9d3850cd971321105497f3dfe153af47",
      state: "2",
      priority: "1",
      short_description: "Incident state synchronization",
      action: "update",
    },
  },
  custom: {
    name: "Custom Webhook",
    title: "Custom Webhook Integration Guide",
    subtitle: "Dispatch HTTP POST payloads from any third-party system or monitoring pipeline.",
    prerequisites: [
      "System capable of dispatching outbound HTTP POST webhooks",
      "JSON formatted payload support",
      "Network access to AIOps ingress endpoint",
    ],
    steps: [
      {
        step: 1,
        title: "Create Custom Integration",
        desc: "Connect a custom integration in AIOps to provision a dedicated incoming webhook URL.",
      },
      {
        step: 2,
        title: "Configure Outbound HTTP POST",
        desc: "Configure your alert manager, CI/CD pipeline, or ticketing system to dispatch an HTTP POST request to your dedicated URL.",
      },
      {
        step: 3,
        title: "Include JSON Headers & Payload",
        desc: "Set header 'Content-Type: application/json' and include incident or ticket details in the request body.",
      },
    ],
    samplePayload: {
      external_id: "TICKET-101",
      title: "Service Latency Warning",
      status: "open",
      priority: "high",
    },
  },
};

export default function WebhookPayloadTesterModal({
  isOpen,
  onClose,
  integration,
  onConnect,
}) {
  const [copiedUrl, setCopiedUrl] = useState(false);
  const [copiedJson, setCopiedJson] = useState(false);
  const [showPayloadSchema, setShowPayloadSchema] = useState(false);
  const [copyError, setCopyError] = useState(null);

  useEffect(() => {
    if (isOpen) {
      setCopiedUrl(false);
      setCopiedJson(false);
      setShowPayloadSchema(false);
      setCopyError(null);
    }
  }, [isOpen, integration?.id, integration?.provider]);

  if (!isOpen || !integration) return null;

  const provider = (integration.provider || "jira").toLowerCase();
  const guide = SETUP_GUIDES[provider] || SETUP_GUIDES.custom;
  const isConfigured = Boolean(integration.isConfigured || (integration.id && integration.id !== provider));

  // If this guide is opened from an active configured instance, compute its live webhook URL
  const webhookPath = integration.webhook_url || (isConfigured ? `/webhooks/${provider}/${integration.id}` : null);
  const fullUrl = webhookPath ? buildFullWebhookUrl(webhookPath, integration.webhook_token) : null;

  const sampleJsonStr = JSON.stringify(guide.samplePayload, null, 2);

  const handleCopyUrl = async () => {
    if (!fullUrl) return;
    setCopyError(null);
    try {
      if (navigator?.clipboard?.writeText) {
        await navigator.clipboard.writeText(fullUrl);
      } else {
        const textarea = document.createElement("textarea");
        textarea.value = fullUrl;
        textarea.style.position = "fixed";
        textarea.style.left = "-9999px";
        document.body.appendChild(textarea);
        textarea.focus();
        textarea.select();
        const success = document.execCommand("copy");
        document.body.removeChild(textarea);
        if (!success) throw new Error("Copy failed");
      }
      setCopiedUrl(true);
      setTimeout(() => setCopiedUrl(false), 2000);
    } catch (err) {
      console.error("Failed to copy URL", err);
      setCopyError("Clipboard access blocked. Please copy manually.");
      setTimeout(() => setCopyError(null), 3500);
    }
  };

  const handleCopyJson = async () => {
    setCopyError(null);
    try {
      if (navigator?.clipboard?.writeText) {
        await navigator.clipboard.writeText(sampleJsonStr);
      } else {
        const textarea = document.createElement("textarea");
        textarea.value = sampleJsonStr;
        textarea.style.position = "fixed";
        textarea.style.left = "-9999px";
        document.body.appendChild(textarea);
        textarea.focus();
        textarea.select();
        const success = document.execCommand("copy");
        document.body.removeChild(textarea);
        if (!success) throw new Error("Copy failed");
      }
      setCopiedJson(true);
      setTimeout(() => setCopiedJson(false), 2000);
    } catch (err) {
      console.error("Failed to copy JSON", err);
      setCopyError("Clipboard access blocked. Please copy manually.");
      setTimeout(() => setCopyError(null), 3500);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={isConfigured && integration.name ? `Setup Guide — ${integration.name}` : `Setup Guide — ${guide.name}`}
      width={640}
    >
      <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
        {copyError && (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              padding: "8px 12px",
              background: "#FEF2F2",
              border: "1px solid #FCA5A5",
              borderRadius: 6,
              color: "#B91C1C",
              fontSize: 12,
            }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <Icon name="alert" size={14} color="#DC2626" />
              <span>{copyError}</span>
            </div>
            <button
              type="button"
              onClick={() => setCopyError(null)}
              style={{
                background: "none",
                border: "none",
                cursor: "pointer",
                padding: "0 2px",
                color: "#DC2626",
              }}
            >
              <Icon name="x" size={13} color="#DC2626" />
            </button>
          </div>
        )}

        {/* Intro Header */}
        <div
          style={{
            background: "#F8FAFC",
            border: "1px solid #E2E8F0",
            borderRadius: 8,
            padding: "12px 16px",
          }}
        >
          <div style={{ fontSize: 13.5, fontWeight: 700, color: "#0F172A", marginBottom: 2 }}>
            {guide.title}
          </div>
          <div style={{ fontSize: 12, color: "#64748B", lineHeight: 1.45 }}>
            {guide.subtitle}
          </div>
        </div>

        {/* CONFIGURED INSTANCE: Show Real Working Webhook URL */}
        {isConfigured && fullUrl ? (
          <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <label style={{ fontSize: 12, fontWeight: 600, color: "#334155", display: "flex", alignItems: "center", gap: 5 }}>
                <Icon name="link" size={13} color="#2563EB" />
                <span>Your Instance Webhook URL (Copy & paste into {guide.name})</span>
              </label>
              <span
                style={{
                  fontSize: 10.5,
                  fontWeight: 600,
                  padding: "2px 7px",
                  borderRadius: 4,
                  background: "#ECFDF5",
                  color: "#065F46",
                  border: "1px solid #A7F3D0",
                }}
              >
                Active Instance Endpoint
              </span>
            </div>

            <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
              <input
                type="text"
                readOnly
                value={fullUrl}
                spellCheck={false}
                style={{
                  flex: 1,
                  fontFamily: "monospace",
                  fontSize: 11.5,
                  background: "#F8FAFC",
                  border: "1px solid #CBD5E1",
                  borderRadius: 6,
                  padding: "8px 12px",
                  color: "#0F172A",
                  outline: "none",
                }}
              />
              <button
                type="button"
                onClick={handleCopyUrl}
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 5,
                  background: copiedUrl ? "#15803D" : "#1D4ED8",
                  color: "#FFFFFF",
                  border: "none",
                  borderRadius: 6,
                  padding: "8px 14px",
                  fontSize: 12,
                  fontWeight: 600,
                  cursor: "pointer",
                  transition: "background 0.15s ease",
                  flexShrink: 0,
                }}
              >
                <Icon name={copiedUrl ? "check" : "copy"} size={13} color="#FFFFFF" />
                <span>{copiedUrl ? "Copied!" : "Copy URL"}</span>
              </button>
            </div>
          </div>
        ) : (
          /* AVAILABLE DIRECTORY: Informational Callout (no dummy URL) */
          <div
            style={{
              background: "#EFF6FF",
              border: "1px solid #BFDBFE",
              borderRadius: 8,
              padding: "11px 14px",
              display: "flex",
              gap: 10,
              alignItems: "flex-start",
            }}
          >
            <Icon name="info" size={16} color="#1D4ED8" style={{ marginTop: 2, flexShrink: 0 }} />
            <div style={{ fontSize: 12, color: "#1E40AF", lineHeight: 1.5 }}>
              <strong>Webhook Pipeline Provisioning:</strong> Each integration you connect receives a dedicated, cryptographically signed Webhook URL and secret token. Once you connect your instance, your active endpoint will be generated and displayed directly on your integration card under <strong>Configured Integrations</strong>.
            </div>
          </div>
        )}

        {/* Prerequisites Checklist (primarily relevant for new connections in Available Directory) */}
        {!isConfigured && Array.isArray(guide.prerequisites) && guide.prerequisites.length > 0 && (
          <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
            <div style={{ fontSize: 11.5, fontWeight: 700, color: "#475569", textTransform: "uppercase", letterSpacing: "0.04em" }}>
              Prerequisites Checklist
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr", gap: 6 }}>
              {guide.prerequisites.map((req, idx) => (
                <div
                  key={idx}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 8,
                    padding: "7px 12px",
                    background: "#F8FAFC",
                    border: "1px solid #E2E8F0",
                    borderRadius: 6,
                    fontSize: 12,
                    color: "#334155",
                  }}
                >
                  <Icon name="check" size={13} color="#16A34A" />
                  <span>{req}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Actionable Step-by-Step Instructions */}
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          <div style={{ fontSize: 11.5, fontWeight: 700, color: "#475569", textTransform: "uppercase", letterSpacing: "0.04em" }}>
            {isConfigured ? "Setup Walkthrough in " + guide.name : "Configuration Steps"}
          </div>

          <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            {guide.steps.map((s) => (
              <div
                key={s.step}
                style={{
                  display: "flex",
                  gap: 12,
                  padding: "10px 14px",
                  background: "#FFFFFF",
                  border: "1px solid #E2E8F0",
                  borderRadius: 8,
                  alignItems: "flex-start",
                }}
              >
                <div
                  style={{
                    width: 22,
                    height: 22,
                    borderRadius: "50%",
                    background: "#EFF6FF",
                    color: "#1D4ED8",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: 11.5,
                    fontWeight: 700,
                    flexShrink: 0,
                    marginTop: 1,
                  }}
                >
                  {s.step}
                </div>
                <div style={{ flex: 1, display: "flex", flexDirection: "column", gap: 3 }}>
                  <div style={{ fontSize: 12.5, fontWeight: 600, color: "#0F172A" }}>
                    {s.title}
                  </div>
                  <div style={{ fontSize: 12, color: "#475569", lineHeight: 1.45 }}>
                    {s.desc}
                  </div>
                  {Array.isArray(s.bullets) && s.bullets.length > 0 && (
                    <ul style={{ margin: "4px 0 0", paddingLeft: 18, fontSize: 11.5, color: "#334155" }}>
                      {s.bullets.map((b, bIdx) => (
                        <li key={bIdx} style={{ marginBottom: 2 }}>{b}</li>
                      ))}
                    </ul>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Collapsible Sample Payload */}
        <div
          style={{
            border: "1px solid #E2E8F0",
            borderRadius: 8,
            overflow: "hidden",
            background: "#F8FAFC",
          }}
        >
          <button
            type="button"
            onClick={() => setShowPayloadSchema((prev) => !prev)}
            style={{
              width: "100%",
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              padding: "10px 14px",
              background: "none",
              border: "none",
              cursor: "pointer",
              fontSize: 12,
              fontWeight: 600,
              color: "#334155",
            }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
              <Icon name="code" size={13} color="#64748B" />
              <span>Expected Event Payload Structure (JSON)</span>
            </div>
            <span style={{ fontSize: 11, color: "#2563EB" }}>
              {showPayloadSchema ? "Hide Payload" : "View Payload"}
            </span>
          </button>

          {showPayloadSchema && (
            <div style={{ padding: "0 14px 14px", borderTop: "1px solid #E2E8F0" }}>
              <div
                style={{
                  display: "flex",
                  justifyContent: "flex-end",
                  padding: "8px 0 6px",
                }}
              >
                <button
                  type="button"
                  onClick={handleCopyJson}
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 4,
                    background: "#FFFFFF",
                    border: "1px solid #CBD5E1",
                    borderRadius: 4,
                    padding: "3px 8px",
                    fontSize: 11,
                    fontWeight: 600,
                    color: "#475569",
                    cursor: "pointer",
                  }}
                >
                  <Icon name={copiedJson ? "check" : "copy"} size={11} />
                  <span>{copiedJson ? "Copied" : "Copy JSON"}</span>
                </button>
              </div>
              <pre
                style={{
                  margin: 0,
                  background: "#0F172A",
                  color: "#38BDF8",
                  padding: "10px 14px",
                  borderRadius: 6,
                  fontFamily: "monospace",
                  fontSize: 11.5,
                  lineHeight: 1.45,
                  overflowX: "auto",
                  maxHeight: 180,
                }}
              >
                {sampleJsonStr}
              </pre>
            </div>
          )}
        </div>

        {/* Modal Footer */}
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            paddingTop: 10,
            borderTop: "1px solid #E2E8F0",
          }}
        >
          <button
            type="button"
            onClick={onClose}
            style={{
              padding: "7px 16px",
              background: "#F1F5F9",
              color: "#475569",
              border: "1px solid #CBD5E1",
              borderRadius: 6,
              fontWeight: 600,
              fontSize: 12.5,
              cursor: "pointer",
            }}
          >
            Close
          </button>

          {!isConfigured && onConnect && (
            <button
              type="button"
              onClick={() => onConnect(provider)}
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 6,
                padding: "7px 18px",
                background: "#1D4ED8",
                color: "#FFFFFF",
                border: "none",
                borderRadius: 6,
                fontWeight: 600,
                fontSize: 12.5,
                cursor: "pointer",
              }}
            >
              <Icon name="plus" size={13} color="#FFFFFF" />
              <span>Connect {guide.name} Now</span>
            </button>
          )}
        </div>
      </div>
    </Modal>
  );
}
